import { useMutation, useQuery, useSuspenseQuery } from '@tanstack/react-query';

import type { UseControlledMutation } from '@/_schemas/types';
import type { ApiError, SearchParams } from '@/_shared';
import { apiClient, queryClient } from '@/_shared';

import type {
  RecruitingJobCategoryCreateSchema,
  RecruitingJobCategoryQueryAllSchema,
  RecruitingJobCategoryQuerySchema,
  RecruitingJobCategoryUpdateSchema,
} from './schemas';

export function useGetRecruitingJobCategoriesControlled(
  { per_page }: SearchParams<{ per_page: number }>,
  enabled = true
) {
  return useQuery({
    queryKey: ['RECRUITING_JOB_CATEGORIES', per_page],
    enabled,
    queryFn: async () => {
      const res = await apiClient.get<RecruitingJobCategoryQueryAllSchema>({
        endpoint: '/api/v1/recruiting-job-categories',
        params: { per_page },
      });
      return res;
    },
  });
}

export const invalidateQueryAllObject = () => {
  queryClient.invalidateQueries({ queryKey: ['RECRUITING_JOB_CATEGORIES'] });
};

export const useGetRecruitingJobCategoriesMutation: UseControlledMutation<RecruitingJobCategoryQuerySchema> = (
  params = {}
) =>
  useMutation({
    ...params,
    mutationFn: async ({ search, per_page }) => {
      return queryClient.fetchQuery({
        queryKey: ['RECRUITING_JOB_CATEGORIES_ASYNC', { search, per_page }],
        staleTime: Infinity,
        queryFn: async () => {
          const res = await apiClient.get<RecruitingJobCategoryQueryAllSchema>({
            endpoint: '/api/v1/recruiting-job-categories',
            params: { search, per_page },
          });
          return res?.data || [];
        },
      });
    },
  });

export function useGetRecruitingJobCategories(apiParamStr: string) {
  return useSuspenseQuery({
    queryKey: ['RECRUITING_JOB_CATEGORIES', apiParamStr],
    queryFn: async () => {
      const res = await apiClient.get<RecruitingJobCategoryQueryAllSchema>({
        endpoint: '/api/v1/recruiting-job-categories',
        params: apiParamStr,
      });
      return res;
    },
  });
}

export const useCreateMutation = () =>
  useMutation<{ data: { id: number } }, ApiError, RecruitingJobCategoryCreateSchema>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: '/api/v1/recruiting-job-categories/',
        payload,
      }),
  });

export function useGetRecruitingJobCategory(id: string) {
  return useSuspenseQuery({
    queryKey: ['RECRUITING_JOB_CATEGORY', id],
    queryFn: async () => {
      const res = await apiClient.get<{ data: RecruitingJobCategoryQuerySchema }>({
        endpoint: `/api/v1/recruiting-job-categories/${id}`,
        preventAuditLog: true,
      });

      return res;
    },
  });
}

export const useUpdateMutation = (id: string) =>
  useMutation<{ data: RecruitingJobCategoryQuerySchema }, ApiError, Partial<RecruitingJobCategoryUpdateSchema>>({
    mutationFn: (payload) =>
      apiClient.request({
        endpoint: `/api/v1/recruiting-job-categories/${id}`,
        method: 'PUT',
        payload,
      }),
  });

export const useDeleteMutation = () =>
  useMutation<unknown, unknown, { id: string }>({
    mutationKey: ['RECRUITING_JOB_CATEGORY_DELETE'],
    mutationFn: ({ id }) =>
      apiClient.request({
        endpoint: `/api/v1/recruiting-job-categories/${id}`,
        method: 'DELETE',
        payload: {},
      }),
  });
